import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { useHealth } from '@/contexts/HealthContext';
import { Plus, Activity, Syringe, Stethoscope, Bell, DollarSign, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TreatmentDialog } from '@/components/ganado/TreatmentDialog';
import { DiagnosisDialog } from '@/components/ganado/DiagnosisDialog';

export default function HealthManagement() {
  const { cattle } = useCattle();
  const { 
    treatments, 
    diagnoses, 
    getUpcomingTreatments, 
    getTotalHealthCosts 
  } = useHealth();
  
  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false);
  const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false);

  const upcomingTreatments = getUpcomingTreatments(30);
  const totalCosts = getTotalHealthCosts();
  const activeDiagnoses = diagnoses.filter(d => d.status !== 'Resuelto').length;

  const recentTreatments = [...treatments]
    .sort((a, b) => new Date(b.applicationDate) - new Date(a.applicationDate))
    .slice(0, 10);

  const recentDiagnoses = [...diagnoses]
    .sort((a, b) => new Date(b.diagnosisDate) - new Date(a.diagnosisDate))
    .slice(0, 5);

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Crítica': return 'destructive';
      case 'Alta': return 'destructive';
      case 'Moderada': return 'default';
      case 'Leve': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'En tratamiento': return 'default';
      case 'Tratado': return 'secondary';
      case 'Resuelto': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-md">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Gestión de Salud</h1>
              <p className="text-muted-foreground">Control veterinario y tratamientos</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setDiagnosisDialogOpen(true)} variant="outline">
            <Stethoscope className="mr-2 h-4 w-4" />
            Nuevo Diagnóstico
          </Button>
          <Button onClick={() => setTreatmentDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Tratamiento
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Tratamientos Totales</p>
                <p className="text-3xl font-bold">{treatments.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-rose-50 flex items-center justify-center">
                <Syringe className="h-6 w-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Próximos (30d)</p>
                <p className="text-3xl font-bold text-warning">{upcomingTreatments.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <Bell className="h-6 w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Diagnósticos Activos</p>
                <p className="text-3xl font-bold text-destructive">{activeDiagnoses}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Costos Totales</p>
                <p className="text-3xl font-bold text-success">${totalCosts.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50 shadow-sm">
        <Tabs defaultValue="treatments" className="w-full">
          <CardHeader>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="treatments">
                <Syringe className="h-4 w-4 mr-2" />
                Tratamientos
              </TabsTrigger>
              <TabsTrigger value="diagnoses">
                <Stethoscope className="h-4 w-4 mr-2" />
                Diagnósticos
              </TabsTrigger>
              <TabsTrigger value="upcoming">
                <Calendar className="h-4 w-4 mr-2" />
                Próximos ({upcomingTreatments.length})
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <TabsContent value="treatments">
            <CardContent>
              <div className="space-y-4">
                {recentTreatments.length > 0 ? (
                  recentTreatments.map((treatment) => {
                    const animal = cattle.find(c => c.id === treatment.cattleId);
                    return (
                      <div key={treatment.id} className="flex items-start justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors">
                        <div className="flex gap-4 flex-1">
                          <div className="w-12 h-12 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                            <Syringe className="h-6 w-6 text-rose-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{treatment.type}</h4>
                              <Badge variant="outline">{treatment.product}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {animal ? `${animal.name} (${animal.tag})` : 'Animal eliminado'}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">Dosis</p>
                                <p className="font-medium">{treatment.dose}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Aplicado</p>
                                <p className="font-medium">{new Date(treatment.applicationDate).toLocaleDateString('es-ES')}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Próxima</p>
                                <p className="font-medium">
                                  {treatment.nextApplicationDate 
                                    ? new Date(treatment.nextApplicationDate).toLocaleDateString('es-ES')
                                    : 'N/A'
                                  }
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Costo</p>
                                <p className="font-medium text-success">${treatment.cost}</p>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">
                              Aplicado por: {treatment.appliedBy} | Indicado por: {treatment.indicatedBy}
                            </div>
                          </div>
                        </div>
                        <Link to={`/ganaderia/detalle/${treatment.cattleId}`}>
                          <Button variant="ghost" size="sm">
                            Ver Animal
                          </Button>
                        </Link>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Syringe className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>No hay tratamientos registrados</p>
                    <Button onClick={() => setTreatmentDialogOpen(true)} className="mt-4">
                      Registrar Primer Tratamiento
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </TabsContent>

          <TabsContent value="diagnoses">
            <CardContent>
              <div className="space-y-4">
                {recentDiagnoses.length > 0 ? (
                  recentDiagnoses.map((diagnosis) => {
                    const animal = cattle.find(c => c.id === diagnosis.cattleId);
                    return (
                      <div key={diagnosis.id} className="flex items-start justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors">
                        <div className="flex gap-4 flex-1">
                          <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                            <Stethoscope className="h-6 w-6 text-destructive" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold">{diagnosis.condition}</p>
                              <Badge variant={getSeverityColor(diagnosis.severity)}>{diagnosis.severity}</Badge>
                              <Badge variant={getStatusColor(diagnosis.status)}>{diagnosis.status}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {animal ? `${animal.name} (${animal.tag})` : 'Animal eliminado'}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-2">
                              <div>
                                <p className="text-muted-foreground text-xs">Diagnóstico</p>
                                <p className="font-medium">{new Date(diagnosis.diagnosisDate).toLocaleDateString('es-ES')}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Diagnosticado por</p>
                                <p className="font-medium">{diagnosis.diagnosedBy}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Costo Total</p>
                                <p className="font-medium text-destructive">${diagnosis.cost}</p>
                              </div>
                            </div>
                            {diagnosis.symptoms && (
                              <p className="text-xs text-muted-foreground mb-1">
                                <span className="font-medium">Síntomas:</span> {diagnosis.symptoms}
                              </p>
                            )}
                            {diagnosis.treatment && (
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">Tratamiento:</span> {diagnosis.treatment}
                              </p>
                            )}
                          </div>
                        </div>
                        <Link to={`/ganaderia/detalle/${diagnosis.cattleId}`}>
                          <Button variant="ghost" size="sm">
                            Ver Animal
                          </Button>
                        </Link>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Stethoscope className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>No hay diagnósticos registrados</p>
                    <Button onClick={() => setDiagnosisDialogOpen(true)} className="mt-4">
                      Registrar Primer Diagnóstico
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </TabsContent>

          <TabsContent value="upcoming">
            <CardContent>
              <div className="space-y-4">
                {upcomingTreatments.length > 0 ? (
                  upcomingTreatments.map((treatment) => {
                    const animal = cattle.find(c => c.id === treatment.cattleId);
                    const daysUntil = Math.ceil(
                      (new Date(treatment.nextApplicationDate) - new Date()) / (1000 * 60 * 60 * 24)
                    );
                    const isUrgent = daysUntil <= 7;

                    return (
                      <div 
                        key={treatment.id} 
                        className={`flex items-start justify-between p-4 rounded-lg border ${
                          isUrgent ? 'bg-warning/5 border-warning' : 'hover:bg-muted/50'
                        } transition-colors`}
                      >
                        <div className="flex gap-4 flex-1">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isUrgent ? 'bg-warning/20' : 'bg-rose-50'
                          }`}>
                            {isUrgent ? (
                              <Bell className="h-6 w-6 text-warning" />
                            ) : (
                              <Calendar className="h-6 w-6 text-rose-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{treatment.type}</h4>
                              {isUrgent && <Badge variant="destructive">Próximo</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {animal ? `${animal.name} (${animal.tag})` : 'Animal eliminado'}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">Producto</p>
                                <p className="font-medium">{treatment.product}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Próxima Aplicación</p>
                                <p className={`font-medium ${isUrgent ? 'text-warning' : ''}`}>
                                  {new Date(treatment.nextApplicationDate).toLocaleDateString('es-ES')}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Días Restantes</p>
                                <p className={`font-medium ${isUrgent ? 'text-warning' : ''}`}>
                                  {daysUntil} día{daysUntil !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Link to={`/ganaderia/detalle/${treatment.cattleId}`}>
                          <Button variant="ghost" size="sm">
                            Ver Animal
                          </Button>
                        </Link>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>No hay tratamientos próximos en los siguientes 30 días</p>
                  </div>
                )}
              </div>
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>

      <TreatmentDialog open={treatmentDialogOpen} onOpenChange={setTreatmentDialogOpen} />
      <DiagnosisDialog open={diagnosisDialogOpen} onOpenChange={setDiagnosisDialogOpen} />
    </div>
  );
}
